import React, { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import Asterisk from '../../../components/common/Asterisk/Asterisk';
import Button from '../../../components/common/Button/Button';
import { Option } from '../../../components/common/Dropdown/Dropdown';
import ErrorMessages from '../../../components/common/ErrorMessages/ErrorMessages';
import ConfirmationDialog from '../../../components/common/Modal/ConfirmationDialog';
import Modal from '../../../components/common/Modal/Modal';
import RadioButton from '../../../components/common/RadioGroup/RadioButton';
import ToolTip from '../../../components/common/ToolTip/ToolTip';
import UnitTable, { UnitColumn } from '../../../components/common/UnitTable/UnitTable'
import MultiresWizard from '../../../components/MultiresWizard/MultiresWizard';
import { getAffordableHousingRates, getElsLookups } from '../../../redux/actions/lookups/lookups';
import {
    getProfileDetails,
    putAptFlagAffordableHousing,
    resetGetProfileDetails
} from '../../../redux/actions/profile/profileDetails';
import { resetSaving } from '../../../redux/actions/saveData/saveData';
import { GetElsLookupsState } from '../../../redux/reducers/getLookups/getElsLookupsReducer';
import { ProfileDetailsState } from '../../../redux/reducers/getProfile/getProfileDetailsReducer';
import { ProfileOverviewState } from '../../../redux/reducers/getProfile/getProfileOverviewReducer';
import { SaveDataState } from '../../../redux/reducers/getSaveData/saveDataReducer';
import { SaveResultState } from '../../../redux/reducers/getSaveData/saveResultReducer';
import {
    selectAffordableHousingRates,
    selectELSLookups,
    selectProfileGetDetails,
    selectProfileGetOverview,
    selectSaveData,
    selectSaveResult
} from '../../../redux/selectors/exportSelectorPaths';
import { WizardExitTrigger, WizardInteractionEventPayload } from '../../../types/GA4Events/WizardEventParams';
import {
    ApartmentFlagType,
    initialApartmentFlag,
    keysOfApartmentFlagType
} from '../../../types/profileDetails/ApartmentFlagType';
import { pickFromProfileDetailsType, ProfileDetailsType } from '../../../types/profileDetails/ProfileDetailsType';
import IncomeUnitType from '../../../types/unit/IncomeUnit';
import {
    FormError,
    getFormErrorByName,
    getFormErrorMessageByName,
    hasError,
    useFormErrorContext
} from '../../../utils/context/FormErrorContext';
import { useNavigation } from '../../../utils/context/NavigationContext';
import trackWizardEvent, { createWizardEventPayload } from '../../../utils/GA4EventHelpers/trackWizardEvent';
import { useReadOnly } from '../../../utils/ReadOnlyContext';
import { bathOptions, convertLookupsToDropdownOptions } from '../../../utils/units';
import "./SuiteBreakdown.css";
import Checkbox from '../../../components/common/Checkbox/Checkbox';
import i18n from '../../../i18n';
import NumberInput from '../../../components/common/NumberInput/NumberInput';
import { GetAffordableHousingRatesState } from '../../../redux/reducers/getLookups/getAffordableHousingRatesReducer';
import { includesAny } from '../../../utils/common';

// The keys the apartment/affordable-housing PUT echoes back in its response `data` - see put_apt_flag_affordable_housing
const persistedApartmentDetailKeys = [
    "hasApartmentFlag",
    "hasAffordableHousingFlag",
    "affordableHousingAgreementCodes",
    "affordableHousingCount"
] as const

type PersistedApartmentDetails = Pick<ProfileDetailsType, typeof persistedApartmentDetailKeys[number]>

// Every save on this page settles into the same saveResultState, so the apartment/affordable-housing response can only be
// told apart by its shape. The PUT always echoes all four keys together, so requiring all of them both identifies that
// response exactly and guarantees the fields read off it are present.
const isPersistedApartmentDetails = (data: SaveResultState["data"]): data is PersistedApartmentDetails =>
    typeof data === "object" && data !== null && persistedApartmentDetailKeys.every((key) => key in data)

function NewSuiteBreakdown() {
    const dispatch = useDispatch()
    const { errors, removeError } = useFormErrorContext()
    const { direction } = useNavigation();
    const { isReadOnly } = useReadOnly()
    const { t } = useTranslation(["suite_breakdown_table", "unit_table", "common"])

    const elsLookupsState: GetElsLookupsState = useSelector(selectELSLookups);
    const affordableHousingRatesState: GetAffordableHousingRatesState = useSelector(selectAffordableHousingRates);
    const profileDetails: ProfileDetailsState = useSelector(selectProfileGetDetails);
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
    const saveData: SaveDataState = useSelector(selectSaveData);
    const saveResultState: SaveResultState = useSelector(selectSaveResult)

    const [getProfileDetailsDispatched, setGetProfileDetailsDispatched] = useState(false)
    const [apartmentFlagData, setApartmentFlagData] = useState<ApartmentFlagType>({ ...initialApartmentFlag });
    const [units, setUnits] = useState<IncomeUnitType[] | undefined>(undefined)
    const [triggerDeleteAllUnits, setTriggerDeleteAllUnits] = useState(false)
    const [triggerRollDownAllUnits, setTriggerRollDownAllUnits] = useState(false)
    const [showWarningModal, setShowWarningModal] = useState(false)
    const [showAffordableHousingWarningModal, setShowAffordableHousingWarningModal] = useState(false)
    const [showRollDownWarningModal, setShowRollDownWarningModal] = useState(false)
    const [isErrorVisible, setIsErrorVisible] = useState<(error: FormError) => boolean>(() => (() => { return true }))
    const [customErrorDisplay, setCustomErrorDisplay] = useState<string | JSX.Element>()
    // PIER2-1829: this tracks both whether the wizard should auto-open once per session and whether it is currently open,
    // so it must not be used as the condition for closing the wizard - it can read false while the wizard is still on screen
    const [showGuidedModal, setShowGuidedModal] = useState(() => {
        try {
            const currentRollNumber = sessionStorage.getItem("rollNumber")
            if (!currentRollNumber) return false // without a roll number we cannot tell if the wizard was already shown, so do not auto-open it
            const shownRolls = sessionStorage.getItem("multiresWizard")?.split(",") ?? []
            return !shownRolls.includes(currentRollNumber)
        } catch {
            return false
        }
    })
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmBody, setConfirmBody] = useState(<></>)
    const [confirmAction, setConfirmAction] = useState(() => (() => { /* */ }))
    const [shouldRefreshUnits, setShouldRefreshUnits] = useState(false);
    const [shouldDisplayUnitTable, setShouldDisplayUnitTable] = useState<boolean>()
    const [activeWizardStep, setActiveWizardStep] = useState(0);
    const campaignStatusCode = sessionStorage.getItem('campaignStatusCode');
    const currentSnapshotId = sessionStorage.getItem("currentSnapshotId") as string
    const propertyCode = sessionStorage.getItem("propertyCode") as string
    const isInternal = sessionStorage.getItem("isInternalUser") === "true" && sessionStorage.getItem("editMode") === "true";
    const isExternalUserAndWIP = !(sessionStorage.getItem("isInternalUser") === "true" && (campaignStatusCode === "EXTERNAL_SUBMITTED" || campaignStatusCode === "INTERNAL_IN_PROGRESS"))
    const isLLE = ["307", "382"].includes(propertyCode)
    const hasMultiresWizard = (!["307", "308", "382", "625", "623", "624", "626"].includes(propertyCode) && !isReadOnly)
    const isMultiresWizardDisabled = isInternal && (campaignStatusCode === "EXTERNAL_SUBMITTED" || campaignStatusCode === "INTERNAL_IN_PROGRESS")
    const profileTypeCode = profileOverview.profile.profile_type_code
    const showResidentialQuestion = (["GLF", "LLE", "HTL", "MTL"].includes(profileTypeCode) ||
        (profileTypeCode === "BIP" && !["340", "341", "352", "361", "602", "470", "472"].includes(propertyCode)))
    const showAffordableHousingQuestion = ["GLF", "BIP", "HTL", "MTL"].includes(profileTypeCode)
    const displayGuidedWizard = showGuidedModal && !isReadOnly && hasMultiresWizard && isExternalUserAndWIP
    const [hasAffordableHousingFlag, setHasAffordableHousingFlag] = useState<boolean | null>(null);
    const [affordableHousingAgreementCodes, setAffordableHousingAgreementCodes] = useState<string[] | null>(null);
    const [affordableHousingCount, setAffordableHousingCount] = useState<number | null>(null);
    const [affordableHousingRateFlag, setAffordableHousingRateFlag] = useState(false)
    const campaignYear = sessionStorage.getItem("activeCampaignYear")
    const rollNum = sessionStorage.getItem("rollNumber")

    // Dropdown options
    const lleSubtypeOptions = convertLookupsToDropdownOptions(elsLookupsState, "landLeaseSubType", "unitSubtypeCode", "unitSubtypeDescription", "unitSubtypeDescriptionFr")
    const apartmentOptions: Option[] = convertLookupsToDropdownOptions(elsLookupsState, "multiresSubType", "unitSubtypeCode", "unitSubtypeDescription", "unitSubtypeDescriptionFr")
    const rowHouseOptions: Option[] = convertLookupsToDropdownOptions(elsLookupsState, "multiresRowHouseSubType", "unitSubtypeCode", "unitSubtypeDescription", "unitSubtypeDescriptionFr")
    const styleOptions: Option[] = convertLookupsToDropdownOptions(elsLookupsState, "unitStyleType", "unitStyleCode", "unitStyleDescription", "unitStyleDescriptionFr")
    const subtypeOptionMap: Record<string, Option[]> = {
        "1": apartmentOptions,
        "2": rowHouseOptions,
        "3": rowHouseOptions
    }

    const handleActiveWizardStep = (step: number) => setActiveWizardStep(step);

    const resetResidentialUnitFields = () => {
        setShowWarningModal(false);

        removeError(getFormErrorByName(errors, "hasApartmentFlag" as string)?.primaryKey as string)
        setApartmentFlagData({ hasApartmentFlag: false });
        if (hasAffordableHousingFlag === true) {
            // answering "No" to the apartment question only resets affordable housing locally; the PUT goes out on save/navigation
            unsetAffordableHousing();
        }
        if (units && units.length > 0) {
            setTriggerDeleteAllUnits(true);
        }
    }

    const unsetAffordableHousing = useCallback(() => {
        setShowAffordableHousingWarningModal(false);
        removeError(getFormErrorByName(errors, "hasAffordableHousingFlag" as string)?.primaryKey as string)
        removeError(getFormErrorByName(errors, "affordableHousingAgreementCodes" as string)?.primaryKey as string)
        removeError(getFormErrorByName(errors, "affordableHousingCount" as string)?.primaryKey as string)
        setHasAffordableHousingFlag(false);
        setAffordableHousingCount(null);
        setAffordableHousingAgreementCodes(null);
    }, [errors, removeError]);

    const rollDownUnits = () => {
        setShowRollDownWarningModal(false);
        setHasAffordableHousingFlag(true)

        dispatch(resetSaving())
        removeError(getFormErrorByName(errors, "hasApartmentFlag" as string)?.primaryKey as string)
        removeError(getFormErrorByName(errors, "hasAffordableHousingFlag" as string)?.primaryKey as string)
        dispatch(putAptFlagAffordableHousing(currentSnapshotId, {
            "has_apartment_flag": apartmentFlagData.hasApartmentFlag,
            "has_affordable_housing_flag": true,
            "affordable_housing_agreement_codes": affordableHousingAgreementCodes,
            "affordable_housing_count": affordableHousingCount,
            "spectral_section": profileTypeCode.toLowerCase() + "-res-units",
            "trigger_spectral": false,
            "property_code": propertyCode,
            "show_residential_question": showResidentialQuestion,
            "show_affordable_housing_question": showAffordableHousingQuestion
        }))

        if (units) {
            setTriggerRollDownAllUnits(true)
        }
    }

    const cancelWizard = (active_step: number, exit_method: WizardExitTrigger, submitted = false) => {
        if (submitted) { // this if block covers case where modal is closing after "Submit" is clicked
            setShouldRefreshUnits(true)
            setShowGuidedModal(false)
            setShowConfirmModal(false) // Fix PIER2-1829: the exit confirmation can be open behind the wizard when the submit settles, closing the wizard would leave it orphaned on screen

            const wizardEventPayload: WizardInteractionEventPayload = createWizardEventPayload({
                wizard_type: "multires",
                wizard_interaction_type: "exit",
                wizard_step: active_step,
                wizard_exit_trigger: exit_method,
            })
            trackWizardEvent(wizardEventPayload)

            return
        }

        // everything from here covers case where user is exiting using the "X" icon or "Cancel" button
        const onConfirmExitWizardModalAction = () => {
            const wizardEventPayload: WizardInteractionEventPayload = createWizardEventPayload({
                wizard_type: "multires",
                wizard_interaction_type: "exit",
                wizard_step: active_step,
                wizard_exit_trigger: exit_method,
            })
            trackWizardEvent(wizardEventPayload)

            setShowConfirmModal(false)
            setShowGuidedModal(false)
        }
        setConfirmAction(() => onConfirmExitWizardModalAction)
        setConfirmBody(<>
            <Trans t={t}
                i18nKey="confirm-exit-wizard"
                ns="common"
                components={{ p: <p /> }}
            />
        </>)
        setShowConfirmModal(true)
    }

    const unsetSubtypeCode = (unit: IncomeUnitType) => {
        if (!unit.styleTypeCode) {
            unit.unitSubtypeCode = null
        }
        return unit
    }

    const setRenovatedCount = (unit: IncomeUnitType) => {
        unit.renovatedUnitCount = unit.renovatedFlag ? 1 : 0
        return unit
    }

    const setVacantCount = (unit: IncomeUnitType) => {
        unit.vacantUnitCount = unit.vacantFlag ? 1 : 0
        return unit
    }

    const updateAffordableHousingAgreementCodes = (code: string, checked: boolean) => {
        const newArr = [...(affordableHousingAgreementCodes ?? [])]
        if (checked && !newArr.includes(code)) {
            newArr.push(code)
        }
        if (!checked && newArr.includes(code)) {
            newArr.splice(newArr.indexOf(code), 1)
        }
        const finalArr = [...new Set(newArr)];
        setAffordableHousingAgreementCodes(finalArr.length === 0 ? null : finalArr)
    }

    let columns: UnitColumn[] = []
    const lleUnitColumns: UnitColumn[] = [
        {
            columnName: "unitSubtypeCode",
            required: true,
            dropdown: lleSubtypeOptions,
            headerClassName: "large-header"
        },
        {
            columnName: "unitCount",
            required: true,
            type: "integer"
        },
        {
            columnName: "netLeasableArea",
            type: "number"
        },
        {
            columnName: "monthlyMarketRent",
            required: true,
            type: "currency",
            headerClassName: "large-header"
        },
    ];

    const baseUnitColumns: UnitColumn[] = [
        {
            columnName: "styleTypeCode",
            required: true,
            dropdown: styleOptions,
            headerClassName: "large-header",
            onPostUnitFieldChange: unsetSubtypeCode,
            tooltipForValue: true
        },
        {
            columnName: "unitSubtypeCode",
            required: true,
            dropdown: subtypeOptionMap,
            dropdownSourceColumn: "styleTypeCode",
            headerClassName: "large-header",
            tooltipForValue: true
        },
        {
            columnName: "unitCount",
            required: true,
            type: "integer"
        },
        {
            columnName: "furnishedFlag",
            type: "boolean",
            headerClassName: "small-header"
        },
        {
            columnName: "bathCount",
            required: true,
            type: "number",
            dropdown: bathOptions
        },
        {
            columnName: "netLeasableArea",
            type: "integer",
            headerClassName: "medium-header"
        },
        {
            columnName: "renovatedUnitCount",
            type: "integer"
        },
        {
            columnName: "vacantUnitCount",
            type: "integer"
        },
        {
            columnName: "monthlyMarketRent",
            required: true,
            type: "currency",
            headerClassName: "large-header"
        },
    ];


    const affordableHousingColumns: UnitColumn[] = [
        {
            columnName: "tenantUnitNumber",
            required: true,
            type: "string",
            headerClassName: "small-header"
        },
        {
            columnName: "styleTypeCode",
            required: true,
            dropdown: styleOptions,
            onPostUnitFieldChange: unsetSubtypeCode,
            headerClassName: "large-header",
            tooltipForValue: true
        },
        {
            columnName: "unitSubtypeCode",
            required: true,
            dropdown: subtypeOptionMap,
            dropdownSourceColumn: "styleTypeCode",
            headerClassName: "large-header",
            tooltipForValue: true
        },
        {
            columnName: "startDate",
            required: true,
            type: "date",
            maxDate: new Date(),
            headerClassName: "large-header"
        },
        {
            columnName: "netLeasableArea",
            required: true,
            type: "integer",
            headerClassName: "medium-header"
        },
        {
            columnName: "bathCount",
            required: true,
            type: "number",
            dropdown: bathOptions
        },
        {
            columnName: "furnishedFlag",
            type: "boolean",
            headerClassName: "small-header"
        },
        {
            columnName: "renovatedFlag",
            type: "boolean",
            headerClassName: "small-header",
            onPostUnitFieldChange: setRenovatedCount
        },
        {
            columnName: "vacantFlag",
            type: "boolean",
            headerClassName: "small-header",
            onPostUnitFieldChange: setVacantCount
        },
        {
            columnName: "monthlyMarketRent",
            required: true,
            type: "currency",
            headerClassName: "medium-header"
        },
        {
            columnName: "affordableUnitFlag",
            type: "boolean",
            headerClassName: "small-header"
        },
    ];

    const internalAffordableHousingColumns: UnitColumn[] = [
        {
            columnName: "rateLeARH",
            headerClassName: "internal-header",
            type: "boolean",
            disabled: true,
            valueFn: (unit: IncomeUnitType) => {
                if (!unit.styleTypeCode || !unit.unitSubtypeCode 
                    || unit.monthlyMarketRent === null || unit.monthlyMarketRent === undefined
                    || !affordableHousingRatesState.data || affordableHousingRatesState.data.length === 0) {
                    return false
                }
                const arhRates = affordableHousingRatesState.data[0]
                if (!Object.hasOwn(subtypeOptionMap, unit.styleTypeCode)) {
                    return false
                }
                const subtypeOptions = subtypeOptionMap[unit.styleTypeCode]
                const subtypeOption = subtypeOptions.find(option => option.value === unit.unitSubtypeCode)
                if (!subtypeOption) {
                    return false
                }
                const unitSubtypeDescription = subtypeOption.text.toLowerCase()
                let bulletinRent: number | null = null
                if (unitSubtypeDescription.includes("1 bed")) {
                    if (!Object.hasOwn(arhRates, "oneBedRent")
                        || arhRates.oneBedRent === null || arhRates.oneBedRent === undefined) {
                        return false
                    }
                    bulletinRent = arhRates.oneBedRent as number
                } else if (unitSubtypeDescription.includes("2 bed")) {
                    if (!Object.hasOwn(arhRates, "twoBedRent")
                        || arhRates.twoBedRent === null || arhRates.twoBedRent === undefined) {
                        return false
                    }
                    bulletinRent = arhRates.twoBedRent as number
                } else if (unitSubtypeDescription.includes("bed")) {
                    if (!Object.hasOwn(arhRates, "threeBedRent")
                        || arhRates.threeBedRent === null || arhRates.threeBedRent === undefined) {
                        return false
                    }
                    bulletinRent = arhRates.threeBedRent as number
                } else if (includesAny(unitSubtypeDescription, ["bachelor", "room", "studio"])) {
                    if (!Object.hasOwn(arhRates, "bachelorRent")
                        || arhRates.bachelorRent === null || arhRates.bachelorRent === undefined) {
                        return false
                    }
                    bulletinRent = arhRates.bachelorRent as number
                }
                if (!bulletinRent) {
                    return false
                }
                return unit.monthlyMarketRent <= bulletinRent
            }
        },
    ]

    const internalColumns: UnitColumn[] = [
        {
            columnName: "buildingIdNumber",
            headerClassName: "internal-header",
            type: 'integer',
            castedTo: 'string'
        },
        {
            columnName: "analysisFlag",
            dropdown: convertLookupsToDropdownOptions(elsLookupsState, "rentAnalysisOptionsNoDesc", "value", "text", "text", false),
            headerClassName: (hasAffordableHousingFlag ? "slim-header " : "medium-header ") + "internal-header",
            type: 'string'
        }
    ];

    if (showAffordableHousingQuestion && hasAffordableHousingFlag) {
        columns.push(...affordableHousingColumns);
    } else if (isLLE) {
        columns.push(...lleUnitColumns);
    } else {
        columns.push(...baseUnitColumns);
    }
    if (isInternal) {
        if (showAffordableHousingQuestion && hasAffordableHousingFlag) {
            columns = [...internalColumns, ...internalAffordableHousingColumns, ...columns];
        } else {
            columns = [...internalColumns, ...columns];
        }
    }

    const applyAllColumnDropdowns: UnitColumn[] = [
        {
            columnName: "analysisFlag",
            dropdown: convertLookupsToDropdownOptions(elsLookupsState, "rentAnalysisOptions", "value", "text", "text", false),
            headerClassName: "large-header internal-header",
            type: 'string'
        }
    ]

    const applyToAllColumnNames = ["analysisFlag", "buildingIdNumber", "unitCount"] // , "monthlyMarketRent"]
    const filterBaseColumnList = columns.filter((c) => applyToAllColumnNames.includes(c.columnName));
    const filterApplyToAllColumnList = applyAllColumnDropdowns.filter((c) => applyToAllColumnNames.includes(c.columnName));
    const commonApplyAllColumnNames = new Set(filterApplyToAllColumnList.map(c => c.columnName));
    const applyToAllColumns = [
        ...filterApplyToAllColumnList,
        ...filterBaseColumnList.filter(i => !commonApplyAllColumnNames.has(i.columnName))
    ]
    // Load lookups
    useEffect(() => {
        if (!elsLookupsState.success && !elsLookupsState.loading) {
            dispatch(getElsLookups(propertyCode))
        }
    }, [dispatch, elsLookupsState, propertyCode])

    useEffect(() => {
        if (!getProfileDetailsDispatched) {
            dispatch(resetGetProfileDetails())
            dispatch(getProfileDetails(currentSnapshotId))
            setGetProfileDetailsDispatched(true)
        }
    }, [currentSnapshotId, dispatch, getProfileDetailsDispatched])

    useEffect(() => {
        if (getProfileDetailsDispatched && !profileDetails.loading) {
            if ((profileDetails.status === 200 && profileDetails.snapshotId === currentSnapshotId)) {
                setApartmentFlagData(pickFromProfileDetailsType(profileDetails.data, keysOfApartmentFlagType))
                setHasAffordableHousingFlag(profileDetails.data.hasAffordableHousingFlag)
                setAffordableHousingAgreementCodes(profileDetails.data.affordableHousingAgreementCodes)
                setAffordableHousingCount(profileDetails.data.affordableHousingCount);
            } else {
                setGetProfileDetailsDispatched(false)
            }
        }
    }, [currentSnapshotId, dispatch, getProfileDetailsDispatched, profileDetails]);

    useEffect(() => {
        if (!showResidentialQuestion || (showResidentialQuestion && apartmentFlagData.hasApartmentFlag === true)) {
            setShouldDisplayUnitTable(true);
        } else {
            setShouldDisplayUnitTable(false);
        }
    }, [apartmentFlagData.hasApartmentFlag, showResidentialQuestion])

    useEffect(() => {
        if (saveResultState.loadingCount >= 0 && saveResultState.status) {
            if (saveResultState.status === 200) {
                // PIER2-1829: the response carries the freshly persisted values, so take them straight into local state
                // instead of setting getProfileDetailsDispatched to false - that would trigger a redundant getProfileDetails fetch resulting GET/PUT profile details race conditions
                if (isPersistedApartmentDetails(saveResultState.data)) {
                    setApartmentFlagData({ hasApartmentFlag: saveResultState.data.hasApartmentFlag })
                    setHasAffordableHousingFlag(saveResultState.data.hasAffordableHousingFlag)
                    setAffordableHousingAgreementCodes(saveResultState.data.affordableHousingAgreementCodes)
                    setAffordableHousingCount(saveResultState.data.affordableHousingCount)
                }
            } else {
                if (saveResultState.message) {
                    toast.error(saveResultState.message)
                }
            }
        }
    }, [saveResultState.data, saveResultState.loadingCount, saveResultState.message, saveResultState.status])

    useEffect(() => {
        if (saveData.saving && (showAffordableHousingQuestion || showResidentialQuestion)) {
            dispatch(resetSaving())
            removeError(getFormErrorByName(errors, "hasApartmentFlag" as string)?.primaryKey as string)
            removeError(getFormErrorByName(errors, "hasAffordableHousingFlag" as string)?.primaryKey as string)
            removeError(getFormErrorByName(errors, "affordableHousingAgreementCodes" as string)?.primaryKey as string)
            removeError(getFormErrorByName(errors, "affordableHousingCount" as string)?.primaryKey as string)
            const triggerSpectral = isInternal || direction !== "backward"
            dispatch(putAptFlagAffordableHousing(currentSnapshotId, {
                "has_apartment_flag": apartmentFlagData.hasApartmentFlag,
                "has_affordable_housing_flag": hasAffordableHousingFlag,
                "affordable_housing_agreement_codes": affordableHousingAgreementCodes,
                "affordable_housing_count": affordableHousingCount,
                "spectral_section": profileTypeCode.toLowerCase() + "-res-units",
                "trigger_spectral": triggerSpectral,
                "property_code": propertyCode,
                "show_residential_question": showResidentialQuestion,
                "show_affordable_housing_question": showAffordableHousingQuestion,
                "is_internal": isInternal
            }))

            const noResidentialUnitsDeclared = showResidentialQuestion && apartmentFlagData.hasApartmentFlag === false; // Hotfix PIER2-1776: Delete units only if question is presented on screen AND value is set to false
            const noAffordableHousingUnitsDeclared = showAffordableHousingQuestion && hasAffordableHousingFlag === false; // Hotfix PIER2-1776: Delete units only if question is presented on screen AND value is set to false

            if (units?.length && noResidentialUnitsDeclared && noAffordableHousingUnitsDeclared) {
                setTriggerDeleteAllUnits(true);
            }
        }
    }, [apartmentFlagData, currentSnapshotId, direction, dispatch, errors, isInternal, profileTypeCode, propertyCode, removeError, saveData.saving, showResidentialQuestion, units, shouldDisplayUnitTable, affordableHousingAgreementCodes, affordableHousingCount, hasAffordableHousingFlag, profileDetails.data.hasAffordableHousingFlag, showAffordableHousingQuestion])

    useEffect(() => {
        if (!affordableHousingRatesState.loading && !affordableHousingRateFlag) {
            if (campaignYear && rollNum) {
                dispatch(getAffordableHousingRates(campaignYear, rollNum.slice(0, 4)))
                setAffordableHousingRateFlag(true)
            }
        }
    }, [affordableHousingRatesState.loading, dispatch, campaignYear, rollNum, affordableHousingRateFlag])

    return (
        <div className={`suite-breakdown-container ${isInternal ? 'internal-table' : ''}`}>
            <ErrorMessages errors={errors} isErrorVisible={isErrorVisible}
                customDisplay={customErrorDisplay} />

            {showResidentialQuestion ?
                <div className='suite-breakdown-rent-to-tenant'>
                    <div className='rent-to-tenant-container'>
                        <p>{!isLLE ? t("tenants") as string : t("tenants-LLE") as string} <Asterisk /></p>
                        <div className='radio-wrapper'>
                            <span className={`side-by-side ${hasError(errors, "hasApartmentFlag") ? "radio-error" : ""}`}>
                                <RadioButton name="hasApartmentFlag"
                                    id="rent-to-tenant-radio-yes"
                                    text={t("confirm") as string}
                                    textPosition="left"
                                    value="true"
                                    ariaLabel={!isLLE ? t("tenants-yes") as string : t("tenants-yes-LLE") as string}
                                    currentValue={apartmentFlagData.hasApartmentFlag}
                                    onChange={() => {
                                        removeError(getFormErrorByName(errors, "hasApartmentFlag" as string)?.primaryKey as string)
                                        setApartmentFlagData({ hasApartmentFlag: true })
                                    }} />
                                <RadioButton name="hasApartmentFlag"
                                    id="rent-to-tenant-radio-no"
                                    text={t("cancel") as string}
                                    textPosition="left"
                                    value="false"
                                    ariaLabel={!isLLE ? t("tenants-no") as string : t("tenants-no-LLE") as string}
                                    currentValue={apartmentFlagData.hasApartmentFlag}
                                    onChange={() => { setShowWarningModal(true) }} />
                            </span>
                            {
                                hasError(errors, "hasApartmentFlag") ?
                                    <div className="input-error-message radio-error">
                                        {getFormErrorMessageByName(errors, "hasApartmentFlag")}
                                    </div>
                                    :
                                    ""
                            }
                        </div>
                    </div>

                </div>
                :
                <></>
            }

            {(displayGuidedWizard && units && ((showResidentialQuestion && apartmentFlagData.hasApartmentFlag) || !showResidentialQuestion)) &&
                <Modal title={t('guided-experience-wizard')}
                    // Fix PIER2-1829: hide X while data is being saved
                    onCloseModal={saveResultState.loading ? undefined : () => cancelWizard(activeWizardStep, "x_icon", false)}
                    class="guided-experience-wizard-modal">
                    <MultiresWizard snapshotId={Number(currentSnapshotId)}
                        onCancel={cancelWizard}
                        unitData={units as unknown as Record<string, unknown>[]}
                        deleteAllUnitsTrigger={setTriggerDeleteAllUnits}
                        rollDownAllUnitsTrigger={setTriggerRollDownAllUnits}
                        isBasePropType={!isLLE}
                        styleOptions={styleOptions}
                        unitTypeSubtypeMap={subtypeOptionMap}
                        bathsOptions={bathOptions}
                        profileDetails={profileDetails}
                        showResidentialQuestion={showResidentialQuestion}
                        sendActiveStepToParent={handleActiveWizardStep}
                        showAffordableHousingQuestion={showAffordableHousingQuestion}
                        hasApartmentFlag={apartmentFlagData.hasApartmentFlag}
                        affordableHousingFields={{
                            "affordableHousingAgreementCodes": affordableHousingAgreementCodes,
                            "affordableHousingCount": affordableHousingCount,
                            "hasAffordableHousingFlag": hasAffordableHousingFlag
                        }}
                        propertyCode={propertyCode} />
                </Modal>
            }

            {showConfirmModal &&
                <ConfirmationDialog
                    onCancel={() => { setShowConfirmModal(false) }}
                    onConfirm={() => confirmAction()}
                    headerClass="no-border"
                    buttonGroupClass="justify-content-center no-border">
                    {confirmBody}
                </ConfirmationDialog>
            }

            {
                (showWarningModal && !isReadOnly) &&
                <Modal onCloseModal={() => setShowWarningModal(false)}>
                    <div className='suite-warning-modal'>
                        <p>{t("delete-all-units")}</p>
                        <div className='suite-warning-modal-buttons'>
                            <Button type='secondary' onClick={resetResidentialUnitFields} >
                                {t("ok")}
                            </Button>
                            <Button type='cancel' onClick={() => setShowWarningModal(false)} >
                                {t("actualCancel")}
                            </Button>
                        </div>
                    </div>
                </Modal>
            }
            {
                (showAffordableHousingWarningModal && !isReadOnly) &&
                <Modal onCloseModal={() => setShowAffordableHousingWarningModal(false)}>
                    <div className='suite-warning-modal'>
                        <p>{t("reset-affordable-housing")}</p>
                        <div className='suite-warning-modal-buttons'>
                            <Button type='secondary' onClick={() => {
                                unsetAffordableHousing();
                                if (units && units.length > 0) {
                                    setTriggerDeleteAllUnits(true)
                                }
                            }} >
                                {t("ok")}
                            </Button>
                            <Button type='cancel' onClick={() => setShowAffordableHousingWarningModal(false)} >
                                {t("actualCancel")}
                            </Button>
                        </div>
                    </div>
                </Modal>
            }
            {
                (showRollDownWarningModal && !isReadOnly) &&
                <Modal onCloseModal={() => setShowRollDownWarningModal(false)}>
                    <div className='suite-warning-modal'>
                        <p>
                            <Trans t={t}
                                i18nKey="reset-warning"
                                components={{ u: <u /> }}
                            />
                        </p>
                        <div className='suite-warning-modal-buttons'>
                            <Button type='secondary' onClick={() => rollDownUnits()} >
                                {t("ok")}
                            </Button>
                            <Button type='cancel' onClick={() => setShowRollDownWarningModal(false)} >
                                {t("actualCancel")}
                            </Button>
                        </div>
                    </div>
                </Modal>
            }
            {
                shouldDisplayUnitTable && showAffordableHousingQuestion &&
                <>
                    <div className='affordable-housing-section'>
                        <p>
                            <b>{t("affordable-housing-new") as string}</b> {t("affordable-housing-title") as string} {profileOverview.profile.current_campaign.year}{t("affordable-housing-title-2") as string} {t("affordable-housing-learn-more") as string} <a href={(t("affordable-housing-learn-more-link") as string)} target='_blank' rel="noreferrer">{t("affordable-housing-learn-more-link") as string}</a>.
                            <ToolTip contents={<FontAwesomeIcon icon={faCircleInfo} className="affordable-housing-learn-more-toolip-icon" />}>
                                <>
                                    <p>{t("affordable-housing-optional-subclass") as string}</p>
                                    <ul>
                                        <li>{t("affordable-housing-optional-subclass-1") as string}</li>
                                        <li>{t("affordable-housing-optional-subclass-2") as string}</li>
                                        <li>{t("affordable-housing-optional-subclass-3") as string}</li>
                                        <li>{t("affordable-housing-optional-subclass-4") as string} <i>{t("affordable-housing-optional-subclass-5") as string}</i>
                                            <ul>
                                                <li>{t("affordable-housing-optional-subclass-5-1") as string}</li>
                                                <li>{t("affordable-housing-optional-subclass-5-2") as string}</li>
                                            </ul>
                                        </li>
                                    </ul>
                                </>
                            </ToolTip>
                        </p>
                        <div className='affordable-housing-inline'>
                            <p>{t("affordable-housing-flag") as string}</p>
                            <div className='radio-wrapper'>
                                <span className={`side-by-side ${hasError(errors, "hasAffordableHousingFlag") ? "radio-error" : ""}`}>
                                    <RadioButton name="hasAffordableHousingFlag"
                                        id="has-affordable-housing-flag-yes"
                                        text={t("confirm") as string}
                                        textPosition="left"
                                        value="true"
                                        currentValue={hasAffordableHousingFlag}
                                        onChange={() => {
                                            if (units && units.length > 0) {
                                                setShowRollDownWarningModal(true)
                                            } else {
                                                rollDownUnits()
                                            }
                                        }} />
                                    <RadioButton name="hasAffordableHousingFlag"
                                        id="has-affordable-housing-flag-no"
                                        text={t("cancel") as string}
                                        textPosition="left"
                                        value="false"
                                        currentValue={hasAffordableHousingFlag}
                                        onChange={() => {
                                            if (units && units.length > 0) {
                                                setShowAffordableHousingWarningModal(true)
                                            } else {
                                                unsetAffordableHousing()
                                            }
                                        }} />
                                </span>
                                {
                                    hasError(errors, "hasAffordableHousingFlag") ?
                                        <div className="input-error-message radio-error">
                                            {getFormErrorMessageByName(errors, "hasAffordableHousingFlag")}
                                        </div>
                                        :
                                        ""
                                }
                            </div>
                        </div>
                        {
                            hasAffordableHousingFlag && showAffordableHousingQuestion && shouldDisplayUnitTable &&
                            <>
                                <p>{t("affordable-rental-house") as string}</p>
                                <p><i>{t("affordable-rental-all-apply") as string}</i></p>
                                <div className='checkbox-wrapper'>
                                    <ul>
                                        {elsLookupsState && elsLookupsState.dropdowns && "affordableHousingAgreement" in elsLookupsState.dropdowns &&
                                            (elsLookupsState.dropdowns["affordableHousingAgreement"] as Record<string, unknown>[]).map((e) =>
                                                <li key={`affordable-rental-option-${e["code"]}`}>
                                                    <Checkbox name="affordableHousingAgreementCodes" id={`affordable-rental-option-${e["code"]}`}
                                                        defaultChecked={affordableHousingAgreementCodes !== null && affordableHousingAgreementCodes.includes(e["code"] as string)}
                                                        onChange={(event) => {
                                                            updateAffordableHousingAgreementCodes(e["code"] as string, event.target.checked)
                                                            removeError("additionalInfo.affordableHousingAgreementCodes")
                                                        }
                                                        }
                                                    />
                                                    <label htmlFor={`affordable-rental-option-${e["code"]}`}>
                                                        {i18n.language === 'fr' ? e["frenchDescription"] as string : e["description"] as string}
                                                    </label>
                                                </li>)
                                        }
                                    </ul>
                                    {
                                        hasError(errors, "affordableHousingAgreementCodes") ?
                                            <div className="input-error-message radio-error">
                                                {getFormErrorMessageByName(errors, "affordableHousingAgreementCodes")}
                                            </div>
                                            :
                                            ""
                                    }
                                </div>
                                <div className='affordable-housing-inline'>
                                    <p>
                                        <Trans t={t}
                                            i18nKey="affordable-housing-count"
                                            components={{ u: <u /> }}
                                        />
                                    </p>
                                    <div className='input-wrapper'>
                                        <NumberInput name="affordableHousingCount" textAlign="left"
                                            negatives={false} decimalPlaces={0} border={true} commas={true} max={9999}
                                            initialValue={affordableHousingCount}
                                            errorBorder="inner"
                                            onChange={(value, _last) => {
                                                setAffordableHousingCount(value)
                                            }}
                                        />
                                    </div>
                                </div>
                                {affordableHousingRatesState.data?.length && isInternal ?
                                    <div className="affordable-housing-rates">
                                        <div className="affordable-housing-rates-container">
                                            <div className="affordable-housing-rates-header">{t("affordable-housing-rates")}{campaignYear ? ` for ${campaignYear}` : ""}</div>
                                            <table className="wizard-table affordable-housing-rates-table" role="presentation">
                                                <thead>
                                                    <tr>
                                                        <th>{t("bachelor")}</th>
                                                        <th>{t("one-bed")}</th>
                                                        <th>{t("two-bed")}</th>
                                                        <th>{t("three-bed")}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr className="text-center">
                                                        <td>${(affordableHousingRatesState.data[0].bachelorRent as string)?.toLocaleString()}</td>
                                                        <td>${(affordableHousingRatesState.data[0].oneBedRent as string)?.toLocaleString()}</td>
                                                        <td>${(affordableHousingRatesState.data[0].twoBedRent as string)?.toLocaleString()}</td>
                                                        <td>${(affordableHousingRatesState.data[0].threeBedRent as string)?.toLocaleString()}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                            <div className="affordable-housing-rates-footer">{t("rates_table_footer")}</div>
                                        </div>
                                    </div> : <></>}
                            </>
                        }
                    </div>
                </>
            }
            {
                ((shouldDisplayUnitTable && ((showAffordableHousingQuestion && hasAffordableHousingFlag !== null) || !showAffordableHousingQuestion)) || triggerDeleteAllUnits || triggerRollDownAllUnits || units === undefined) &&
                <>
                    <UnitTable
                        propClass={(shouldDisplayUnitTable && ((showAffordableHousingQuestion && hasAffordableHousingFlag !== null) || !showAffordableHousingQuestion)) ? "" : "transparent"}
                        units={units ? units : []}
                        setUnits={(units) => setUnits(units as IncomeUnitType[])}
                        deleteColumn
                        isInternal={isInternal}
                        translationScope={`suite_breakdown`}
                        columns={columns}
                        applyToAllColumns={applyToAllColumns}
                        unitType='3'
                        setIsErrorVisible={setIsErrorVisible}
                        setCustomErrorDisplay={setCustomErrorDisplay}
                        triggerDeleteAllUnits={triggerDeleteAllUnits}
                        setTriggerDeleteAllUnits={setTriggerDeleteAllUnits}
                        triggerRollDownAllUnits={triggerRollDownAllUnits}
                        setTriggerRollDownAllUnits={setTriggerRollDownAllUnits}
                        shouldRefreshUnits={shouldRefreshUnits}
                        setShouldRefreshUnits={setShouldRefreshUnits}
                        showAffordableHousingTable={showAffordableHousingQuestion && hasAffordableHousingFlag === true}
                        affordableHousingUnitCount={affordableHousingCount}
                        guidedWizardButton={hasMultiresWizard ?
                            <ToolTip contents=
                                {
                                    <Button text={<FontAwesomeIcon icon={faWandMagicSparkles} />}
                                        disabled={isMultiresWizardDisabled}
                                        ariaLabel='guidedWizard'
                                        onClick={() => {
                                            setShowGuidedModal(true);
                                            const wizardEventPayload: WizardInteractionEventPayload = createWizardEventPayload({
                                                wizard_type: "multires",
                                                wizard_interaction_type: "enter",
                                                wizard_entry_trigger: "wizard_button_click"
                                            })
                                            trackWizardEvent(wizardEventPayload)
                                        }} />
                                }>
                                {t("wizard-tooltip")
                                }
                            </ToolTip>
                            :
                            undefined
                        }
                    />
                </>
            }
        </div>
    )
}

export default NewSuiteBreakdown
